import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, FileDown, Sheet as SheetIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { exportPDF, exportXLSX } from "@/lib/exporters";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { NativeSelect } from "@/components/Field";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";

const KINDS = [{ value: "tithes", label: "Tithe" }, { value: "donations", label: "Conference Collection" }, { value: "progress", label: "Progress Report" }, { value: "registrations", label: "Conference Registration" }];
const ACTIONS = [{ value: "create", label: "Added" }, { value: "update", label: "Changed" }, { value: "delete", label: "Deleted" }];
const BADGE = { create: "bg-emerald-50 text-emerald-800", update: "bg-amber-50 text-amber-900", delete: "bg-red-50 text-red-800" };
const label = (list, v) => list.find((x) => x.value === v)?.label || v;
const when = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: "America/Chicago", dateStyle: "medium", timeStyle: "short" });
const show = (v, field) => (v === null || v === undefined || v === "" ? "—" : field === "amount" ? money(v) : String(v));
const fieldName = (f) => f.replace(/_id$/, "").replace(/_/g, " ");

function Changes({ changes }) {
  return (
    <table className="w-full text-xs">
      <thead><tr className="text-left text-slate-500"><th className="py-1 pr-4">Field</th><th className="py-1 pr-4">Before</th><th className="py-1">After</th></tr></thead>
      <tbody>
        {changes.map((c) => (
          <tr key={c.field} className="border-t border-slate-200">
            <td className="py-1.5 pr-4 font-semibold capitalize">{fieldName(c.field)}</td>
            <td className="py-1.5 pr-4 text-red-700 line-through decoration-red-300">{show(c.before, c.field)}</td>
            <td className="py-1.5 text-emerald-800">{show(c.after, c.field)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const changeText = (r) => r.changes.map((c) => `${fieldName(c.field)}: ${show(c.before, c.field)} → ${show(c.after, c.field)}`).join("; ");

function toSection(rows) {
  return {
    title: "Activity Log",
    head: ["When (Central)", "Who", "Email", "Role", "Action", "Type", "Church", "Record", "Changes"],
    body: rows.map((r) => [when(r.at), r.user_name, r.user_email, r.user_role === "master_admin" ? "Admin" : "Church user", label(ACTIONS, r.action), label(KINDS, r.kind), r.church_name, r.summary, changeText(r)]),
  };
}

function describeFilters(f, churches) {
  const parts = [];
  if (f.kind) parts.push(label(KINDS, f.kind));
  if (f.action) parts.push(label(ACTIONS, f.action));
  if (f.church_id) parts.push(churches.find((c) => c.id === f.church_id)?.name || "");
  if (f.start || f.end) parts.push(`${f.start || "start"} to ${f.end || "today"}`);
  return parts.length ? parts.join(" · ") : "All activity";
}

export default function ActivityLog() {
  const { churches } = useApp();
  const [f, setF] = useState({});
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/activity", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [f]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Full-access admins only" title="Activity Log" subtitle="Who added, changed or deleted records, and when (Central time).">
        <Button variant="outline" disabled={!rows.length} data-testid="activity-export-pdf-btn"
          onClick={() => exportPDF(`MWJK-activity-log-${new Date().toISOString().slice(0, 10)}.pdf`, "MWJK Activity Log", `${describeFilters(f, churches)} · ${rows.length} entries · Printed ${when(new Date().toISOString())}`, [toSection(rows)])}>
          <FileDown className="mr-2 h-4 w-4" /> PDF
        </Button>
        <Button disabled={!rows.length} data-testid="activity-export-xlsx-btn"
          onClick={() => exportXLSX(`MWJK-activity-log-${new Date().toISOString().slice(0, 10)}.xlsx`, [toSection(rows)])}>
          <SheetIcon className="mr-2 h-4 w-4" /> Spreadsheet
        </Button>
      </PageHeader>
      <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-5 md:items-end" data-testid="activity-filters">
        <NativeSelect value={f.kind} onChange={set("kind")} placeholder="All record types" options={KINDS} testid="activity-filter-kind" />
        <NativeSelect value={f.action} onChange={set("action")} placeholder="All actions" options={ACTIONS} testid="activity-filter-action" />
        <NativeSelect value={f.church_id} onChange={set("church_id")} placeholder="All churches" options={churches.map((c) => ({ value: c.id, label: c.name }))} testid="activity-filter-church" />
        <label className="space-y-1 text-xs font-semibold uppercase tracking-wide text-slate-500">From<Input type="date" className="h-11" value={f.start || ""} onChange={set("start")} data-testid="activity-filter-start" /></label>
        <label className="space-y-1 text-xs font-semibold uppercase tracking-wide text-slate-500">To<Input type="date" className="h-11" value={f.end || ""} onChange={set("end")} data-testid="activity-filter-end" /></label>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="activity-table">
          <thead><tr className="border-b bg-[hsl(var(--sand))] text-left">
            <th className="w-8 px-3 py-3" /><th className="px-4 py-3">When</th><th className="px-4 py-3">Who</th><th className="px-4 py-3">Action</th>
            <th className="px-4 py-3">Type</th><th className="px-4 py-3">Church</th><th className="px-4 py-3">Record</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-testid="activity-empty">No activity for these filters yet.</td></tr>}
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr className={`border-b border-slate-100 ${r.changes.length ? "cursor-pointer hover:bg-slate-50" : ""}`} onClick={() => r.changes.length && setOpen(open === r.id ? null : r.id)} data-testid={`activity-row-${r.id}`}>
                  <td className="px-3 py-3 text-slate-400">{r.changes.length > 0 && (open === r.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}</td>
                  <td className="whitespace-nowrap px-4 py-3">{when(r.at)}</td>
                  <td className="px-4 py-3"><div className="font-semibold">{r.user_name}</div><div className="text-xs text-slate-500">{r.user_role === "master_admin" ? "Admin" : "Church user"} · {r.user_email}</div></td>
                  <td className="px-4 py-3"><span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${BADGE[r.action]}`} data-testid={`activity-action-${r.id}`}>{label(ACTIONS, r.action)}{r.action === "update" && ` (${r.changes.length})`}</span></td>
                  <td className="px-4 py-3">{label(KINDS, r.kind)}</td>
                  <td className="px-4 py-3">{r.church_name}</td>
                  <td className="px-4 py-3 text-slate-700">{r.summary}</td>
                </tr>
                {open === r.id && <tr className="bg-[hsl(var(--sand))]"><td /><td colSpan={6} className="px-4 py-3" data-testid={`activity-changes-${r.id}`}><Changes changes={r.changes} /></td></tr>}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length >= 1000 && <p className="text-on-bg-muted text-xs">Showing the latest 1,000 entries. Use the filters to narrow down.</p>}
    </div>
  );
}
